import { useFetch } from 'shared/hooks/use-fetch'
import { RequestMethodsEnum } from 'shared/hooks/use-fetch/types'
import { useOAuthLogin } from 'shared/hooks/use-oauth-login'
import useUser from 'shared/hooks/use-user'
import { INTEGRATIONS_API } from '../api'
import { AuthUrlInterface, SendZoomIntegrationCodeProps } from '../types'

export const useIntegration = ({
  onError,
  onSuccess,
  setIsLoading,
}: {
  onError: (_: Error) => void
  onSuccess: () => void
  setIsLoading: (isLoading: boolean) => void
}) => {
  const { user } = useUser()
  const { fetcher } = useFetch<RequestMethodsEnum.get, AuthUrlInterface>({
    method: RequestMethodsEnum.get,
  })

  const { fetcher: removeIntegrationFetcher } = useFetch<RequestMethodsEnum.delete>({
    method: RequestMethodsEnum.delete,
  })

  const { openAuthWindow } = useOAuthLogin()

  const subscribeOnMessageFromCallbackPage = (authWindow: WindowProxy) => {
    const handleMessage = async (
      event: MessageEvent<{ code: string; state: string; error: string }>,
    ) => {
      if (event.origin !== window.location.origin) return

      const { code, error, state } = event.data

      if (event.source !== authWindow) return
      if (code && state) {
        try {
          await sendZoomIntegrationCode({ code, state })

          onSuccess()
        } catch (e) {
          onError(e as unknown as Error)
        }
      } else if (error) {
        onError(new Error(error))
      }

      window.removeEventListener('message', handleMessage)
    }

    window.addEventListener('message', handleMessage)
  }

  const openZoomLogin = async () => {
    setIsLoading(true)

    const { url } = await fetcher(`${INTEGRATIONS_API}/zoom-integration/user/authorize`)

    setIsLoading(false)

    await openAuthWindow({
      locationHref: url,
      subscribeOnMessageFromCallbackPage,
      onError,
    })
  }

  const sendZoomIntegrationCode = async ({ code, state }: SendZoomIntegrationCodeProps) => {
    if (!user) return

    await fetcher(
      `${INTEGRATIONS_API}/zoom-integration/user/oauth-callback?code=${code}&state=${state}`,
    )
  }

  const removeIntegration = async () => {
    if (!user) return
    setIsLoading(true)
    await removeIntegrationFetcher(`${INTEGRATIONS_API}/zoom-integration/user/revoke`)
    setIsLoading(false)
  }

  return {
    openZoomLogin,
    sendZoomIntegrationCode,
    removeIntegration,
  }
}
